import { useState, useEffect, useRef, type FormEvent, type KeyboardEvent } from 'react';
import { MessageSquare, ListTodo } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectProjectMessages, selectProjectTasks, selectProjectId, selectUserRole, selectUserId } from '@/store/selectors';
import type { ProjectTask } from '@/types/task';
import { addMessage, removeMessage } from '../../app/features/project/projectSlice';
import { sendMessage } from '../../apiServices/messageApi';
import { useNotification } from '@/hooks/useNotification';
import { Button } from '@/components/ui/Button';
import { ProjectChatMessages } from './ProjectChatMessages';
import { ProjectChatTasks } from './ProjectChatTasks';

interface ProjectChatProps {
  messagesTabOpen: boolean;
  updateMessagesTabOpen: (open: boolean) => void;
  updateSelectedTask: (task: ProjectTask | null) => void;
  updateModalOpen: (type: string, params?: Record<string, unknown>) => void;
}

export function ProjectChat({
  messagesTabOpen,
  updateMessagesTabOpen,
  updateSelectedTask,
  updateModalOpen,
}: ProjectChatProps) {
  const dispatch = useAppDispatch();
  const { notify } = useNotification();

  const messages = useAppSelector(selectProjectMessages) as unknown[] | null;
  const tasks = useAppSelector(selectProjectTasks) as unknown[] | null;
  const projectId = useAppSelector(selectProjectId) as string;
  const userRole = useAppSelector(selectUserRole) as string | null;
  const uid = useAppSelector(selectUserId);
  const user = useAppSelector((state) => state.user);
  const users = useAppSelector((state) => state.project.users) as Array<{ uid: string; [k: string]: unknown }>;

  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messagesTabOpen, messages, tasks]);

  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const text = inputValue.trim();
    if (!text) return;

    // Optimistic: show immediately
    const tempId = `temp-${Date.now()}`;
    const optimisticMsg = {
      created: new Date().toString(),
      content: text,
      sender: user.uid,
      id: tempId,
      userData: users.find((u) => u.uid === user.uid),
    };
    dispatch(addMessage(optimisticMsg));
    setInputValue('');
    setIsSending(true);

    try {
      const res = await sendMessage(projectId, text);
      const newMessage = res.response.Item;
      // Replace temp message with real one
      dispatch(removeMessage(tempId));
      dispatch(
        addMessage({
          created: newMessage?.created?.S || optimisticMsg.created,
          content: newMessage?.msgbody?.S,
          sender: user.uid || newMessage?.sender?.S,
          id: newMessage?.msgid?.S,
          userData: users.find((u) => u.uid === (user.uid || newMessage?.sender?.S)),
        }),
      );
    } catch {
      dispatch(removeMessage(tempId));
      setInputValue(text);
      notify({ id: String(Date.now()), type: 'error', message: 'Could not send message' });
    }
    setIsSending(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      formRef.current?.requestSubmit();
    }
  };

  const tabClass = (active: boolean) =>
    `flex w-1/2 cursor-pointer items-center justify-center gap-2 border-t px-3 py-2 text-sm font-medium transition-colors ${
      active
        ? 'bg-info text-white border-info'
        : 'bg-card text-info border-border hover:bg-secondary'
    }`;

  return (
    <div className="flex w-full flex-col rounded-lg border border-border">
      {/* Tabs */}
      <div className="flex">
        <button
          type="button"
          className={`${tabClass(messagesTabOpen)} rounded-tl-lg border-l`}
          onClick={() => updateMessagesTabOpen(true)}
        >
          <MessageSquare className="size-4" />
          Messages
        </button>
        <button
          type="button"
          className={`${tabClass(!messagesTabOpen)} rounded-tr-lg border-r`}
          onClick={() => updateMessagesTabOpen(false)}
        >
          <ListTodo className="size-4" />
          Tasks
        </button>
      </div>

      {/* Content */}
      <div ref={listRef} className="h-[32rem] overflow-y-auto border-x border-border bg-card">
        <ProjectChatMessages
          messages={messages as Parameters<typeof ProjectChatMessages>[0]['messages']}
          isVisible={messagesTabOpen}
          projectId={projectId}
          uid={uid}
          userRole={userRole}
          updateModalOpen={updateModalOpen}
        />
        {tasks && (
          <ProjectChatTasks
            tasks={tasks as Parameters<typeof ProjectChatTasks>[0]['tasks']}
            updateSelectedTask={updateSelectedTask as Parameters<typeof ProjectChatTasks>[0]['updateSelectedTask']}
            isVisible={!messagesTabOpen}
            projectId={projectId}
            uid={uid}
            userRole={userRole}
            updateModalOpen={updateModalOpen}
          />
        )}
      </div>

      {/* Message form */}
      {messagesTabOpen && (
        <form ref={formRef} onSubmit={handleSubmit} className="flex h-24 border border-border rounded-b-lg bg-card">
          <div className="flex-7 p-1">
            <textarea
              className="size-full resize-none border-none p-2 text-sm outline-none placeholder:text-muted-foreground"
              placeholder="Write a message..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="flex flex-3 items-center justify-center border-l border-border p-2">
            <Button type="submit" disabled={isSending} className="w-full">
              {isSending ? 'Sending...' : 'Post'}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
